import React, { useEffect, useState } from 'react';
import { Grid, Card, CardContent, Typography, Stack, Box, CircularProgress } from '@mui/material';
import { AccountBalanceWallet, AccessTime, Language, Hotel, Person } from '@mui/icons-material';
import axios from 'axios';

// API base URL - adjust according to your setup
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
// Website base URL
const WEBSITE_BASE_URL = process.env.REACT_APP_WEBSITE_BASE_URL;

const StatisticsCards = () => {
  const [totalChefs, setTotalChefs] = useState(null);
  const [totalReceptionists, setTotalReceptionists] = useState(null);
  const [totalBartenders, setTotalBartenders] = useState(null);
  const [revenueToday, setRevenueToday] = useState(null);
  const [hotelCode, setHotelCode] = useState(null);
  const [staffCode, setStaffCode] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());

  // Update time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Get hotel code and staff code from localStorage
  useEffect(() => {
    const userProfile = JSON.parse(localStorage.getItem('userProfile'));
    const code = userProfile?.hotel_code || null;
    // Use staff_id as staff_code (since your PHP API uses created_by field which is staff ID)
    const staff_code = userProfile?.staff_id || userProfile?.id || null;
    
    setHotelCode(code);
    setStaffCode(staff_code);
    
    if (!code || !staff_code) {
      console.warn('Hotel code or staff code not found in localStorage');
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!hotelCode || !staffCode) return;

    const fetchData = async () => {
      setLoading(true);
      
      try {
        // Fetch staff payments with both required parameters
        const staffPaymentsRes = await axios.get(
          `${API_BASE_URL}/hotel/staff/reception/get_staff_payments_today.php?staff_code=${staffCode}&hotel_code=${hotelCode}`
        );

        console.log('Staff payments response:', staffPaymentsRes.data);

        // Use the staff-specific payment data
        if (staffPaymentsRes.data.success) {
          setRevenueToday(staffPaymentsRes.data.data.total_amount);
        } else {
          console.error('Error fetching staff payments:', staffPaymentsRes.data.error);
          setRevenueToday(0);
        }

        // Set other stats to 0 or fetch from other APIs if needed
        setTotalChefs(0);
        setTotalReceptionists(0);
        setTotalBartenders(0);

      } catch (error) {
        console.error('Error fetching statistics:', error);
        setTotalChefs(0);
        setTotalReceptionists(0);
        setTotalBartenders(0);
        setRevenueToday(0);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [hotelCode, staffCode]);

  // Format time to HH:MM:SS AM/PM
  const formatTime = (date) => {
    return date.toLocaleTimeString('en-US', {
      hour12: true,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  // Format currency
  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '₦0.00';
    
    return `₦${parseFloat(amount).toLocaleString(undefined, { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    })}`;
  };

  // Handle card clicks
  const handleCardClick = (url) => {
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const cards = [
    {
      title: 'Time',
      value: formatTime(currentTime),
      icon: <AccessTime fontSize="medium" />,
      gradient: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
      glossy: true,
      shadow: '0 4px 20px rgba(25, 118, 210, 0.3)',
      url: null
    },
    {
      title: 'Website',
      value: 'Visit Site',
      icon: <Language fontSize="medium" />,
      gradient: 'linear-gradient(135deg, #ffffff 0%, #f5f5f5 100%)',
      textColor: '#1976d2',
      glossy: true,
      shadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
      url: WEBSITE_BASE_URL
    },
    {
      title: 'Rooms',
      value: 'View Rooms',
      icon: <Hotel fontSize="medium" />,
      gradient: 'linear-gradient(135deg, #66bb6a 0%, #4caf50 100%)',
      glossy: true,
      shadow: '0 4px 20px rgba(76, 175, 80, 0.3)',
      url: `${WEBSITE_BASE_URL}/RoomsSuites`
    },
    {
      title: 'My Today\'s Sales',
      value: formatCurrency(revenueToday),
      icon: <AccountBalanceWallet fontSize="medium" />,
      gradient: 'linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%)',
      glossy: true,
      shadow: '0 4px 20px rgba(46, 125, 50, 0.3)',
      url: null,
      
    },
  ];

  if (loading) {
    return (
      <Grid container spacing={3}>
        {cards.map((card, idx) => (
          <Grid item xs={12} sm={6} lg={4} key={idx}>
            <Card
              sx={{
                height: card.subtitle ? 90 : 80,
                minWidth: 280,
                cursor: 'default',
                background: card.gradient,
                color: card.textColor || 'white',
                borderRadius: 3,
                boxShadow: 6,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              <CircularProgress size={24} sx={{ color: card.textColor || 'white' }} />
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  }

  return (
    <Grid container spacing={3}>
      {cards.map((card, idx) => (
        <Grid item xs={12} sm={6} lg={4} key={idx}>
          <Card
            onClick={() => handleCardClick(card.url)}
            sx={{
              height: card.subtitle ? 90 : 80,
              minWidth: 280,
              cursor: card.url ? 'pointer' : 'default',
              background: card.gradient,
              color: card.textColor || 'white',
              borderRadius: 3,
              boxShadow: card.shadow,
              transition: 'all 0.3s ease',
              position: 'relative',
              overflow: 'hidden',
              '&::before': card.glossy ? {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '30%',
                background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.15) 0%, transparent 100%)',
                borderRadius: '8px 8px 0 0',
              } : {},
              '&:hover': {
                transform: card.url ? 'scale(1.02)' : 'none',
                boxShadow: card.url ? 8 : card.shadow,
              },
            }}
          >
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                <Box flex={1}>
                  <Typography variant="subtitle2" sx={{ opacity: 0.9, fontSize: '0.875rem', mb: 0.5 }}>
                    {card.title}
                  </Typography>
                  <Typography variant="h5" fontWeight={600} sx={{ fontSize: '1.5rem' }}>
                    {card.value}
                  </Typography>
                  {card.subtitle && (
                    <Typography variant="caption" sx={{ opacity: 0.8, fontSize: '0.7rem' }}>
                      {card.subtitle}
                    </Typography>
                  )}
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    backgroundColor: card.textColor ? 'rgba(25, 118, 210, 0.1)' : 'rgba(255, 255, 255, 0.2)',
                    backdropFilter: 'blur(10px)',
                    flexShrink: 0,
                    border: card.textColor ? '1px solid rgba(25, 118, 210, 0.2)' : '1px solid rgba(255, 255, 255, 0.3)',
                  }}
                >
                  {card.icon}
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
};

export default StatisticsCards;